import { useQuery } from "@tanstack/react-query";
import { Bike, RefreshCcw } from "lucide-react";
import L from "leaflet";
import { adminLiveApi } from "@/features/delivery/api";
import { LiveMap } from "@/components/delivery/LiveMap";

const riderIcon = L.divIcon({
    html: `<div style="background:#10b981;color:#fff;border-radius:50%;width:34px;height:34px;display:grid;place-items:center;box-shadow:0 4px 14px rgba(16,185,129,.4);border:3px solid #fff;font-size:16px">🛵</div>`,
    className: "",
    iconSize: [34, 34],
    iconAnchor: [17, 17],
});

export default function AdminLiveMap() {
    const { data, isLoading, refetch } = useQuery({
        queryKey: ["admin", "riders", "live"],
        queryFn: adminLiveApi.riders,
        refetchInterval: 5000,
    });

    const markers =
        data?.items
            .filter((r) => r.current_location)
            .map((r) => ({
                position: { lat: r.current_location!.lat, lng: r.current_location!.lng },
                label: `${r.full_name} · ${r.active_delivery?.number ?? "idle"}`,
                icon: riderIcon,
            })) || [];

    return (
        <div className="space-y-5" data-testid="admin-livemap-page">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-extrabold sm:text-3xl">Live fleet map</h1>
                    <p className="text-sm text-muted-foreground">
                        {data
                            ? `${data.items.length} online riders · updates every 5 s`
                            : "Loading…"}
                    </p>
                </div>
                <button
                    onClick={() => refetch()}
                    className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs font-semibold hover:bg-muted"
                    data-testid="admin-livemap-refresh"
                >
                    <RefreshCcw className="h-3.5 w-3.5" /> Refresh
                </button>
            </div>

            {isLoading ? (
                <div className="grid h-72 place-items-center rounded-2xl border border-border/70 bg-card text-sm text-muted-foreground">
                    Fetching live positions…
                </div>
            ) : markers.length === 0 ? (
                <div className="grid h-72 place-items-center rounded-2xl border border-dashed border-border/70 bg-card/50 text-center text-sm text-muted-foreground">
                    <div>
                        <Bike className="mx-auto h-6 w-6 text-brand" />
                        <p className="mt-1">
                            No riders reporting GPS right now. Ask a rider to open the app
                            and enable location.
                        </p>
                    </div>
                </div>
            ) : (
                <LiveMap markers={markers} height={480} testId="admin-live-map" />
            )}

            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {(data?.items || []).map((r) => (
                    <div
                        key={r.rider_id}
                        data-testid={`admin-rider-live-${r.rider_id}`}
                        className="rounded-2xl border border-border/70 bg-card p-3 text-sm shadow-soft"
                    >
                        <div className="flex items-center justify-between">
                            <p className="font-bold">{r.full_name}</p>
                            <span
                                className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
                                    r.active_delivery
                                        ? "bg-brand/10 text-brand"
                                        : "bg-slate-500/10 text-slate-600"
                                }`}
                            >
                                {r.active_delivery ? r.delivery_status : "idle"}
                            </span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                            {r.vehicle_type} · {r.active_delivery?.number || "no order"}
                        </p>
                        {r.current_location ? (
                            <p className="mt-1 text-[10px] text-muted-foreground">
                                {r.current_location.lat.toFixed(4)}, {r.current_location.lng.toFixed(4)}
                            </p>
                        ) : (
                            <p className="mt-1 text-[10px] text-muted-foreground">No GPS yet</p>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}
